"use client";

import { useState } from "react";
import { ChevronDown, Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { RangeInput } from "@/components/ui/range-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Link } from "@/components/common/Link";
const eras = ["Luxury", "Boutique", "Resort", "Business", "Heritage"];
const properties = [{
  id: 1,
  era: "Budget Hotel · Kenya",
  price: "45% Growth",
  name: "Nairobi Urban Stay",
  address: "Revenue Optimization Strategy",
  specs: "OTA AUDIT / PRICING / TECH STACK",
  badge: "CASE STUDY",
  badgeType: "new",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_nairobi_stay.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_nairobi_stay.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_nairobi_stay.webp-wvc-sizes",
  notes: "Implemented a data-driven OTA strategy that increased direct bookings by 45% within six months. Optimized channel management and tech stack integration."
}, {
  id: 2,
  era: "Boutique Hotel · Thailand",
  price: "30% Growth",
  name: "Phuket Coastal Retreat",
  address: "Revenue Management Consulting",
  specs: "DYNAMIC PRICING / OTA LISTINGS",
  badge: "FEATURED",
  badgeType: "open",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_phuket_retreat.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_phuket_retreat.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_phuket_retreat.png-wvc-sizes",
  notes: "Revamped revenue management processes, resulting in a 30% increase in RevPAR through dynamic pricing and improved OTA visibility."
}, {
  id: 3,
  era: "Resort Chain · Maldives",
  price: "50% Growth",
  name: "Maldives Island Resort",
  address: "Revenue Growth Strategy",
  specs: "TECH IMPLEMENTATION / AUDIT",
  badge: "",
  badgeType: "",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_maldives_resort.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_maldives_resort.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_maldives_resort.png-wvc-sizes",
  notes: "Comprehensive revenue audit and tech stack overhaul led to a 50% growth in total revenue across the resort chain."
}, {
  id: 4,
  era: "5 Star Resort · India",
  price: "35% Growth",
  name: "Goa Luxury Palace",
  address: "Revenue Optimization",
  specs: "OTA MANAGEMENT / AUDIT",
  badge: "CASE STUDY",
  badgeType: "new",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_goa_palace.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_goa_palace.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_goa_palace.png-wvc-sizes",
  notes: "Strategic OTA listing optimization and revenue management consulting helped this luxury property achieve a 35% increase in annual revenue."
}, {
  id: 5,
  era: "Business Hotel · UK",
  price: "25% Growth",
  name: "London City Suites",
  address: "Revenue Management",
  specs: "DYNAMIC PRICING / FORECASTING",
  badge: "FEATURED",
  badgeType: "open",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_london_suites.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_london_suites.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_london_suites.webp-wvc-sizes",
  notes: "Optimized forecasting and dynamic pricing models to capture higher corporate demand, yielding a 25% revenue uplift."
}, {
  id: 6,
  era: "Eco Lodge · Zanzibar",
  price: "40% Growth",
  name: "Zanzibar Eco Haven",
  address: "Revenue Growth",
  specs: "OTA LISTINGS / MARKETING",
  badge: "",
  badgeType: "",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_zanzibar_haven.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_zanzibar_haven.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_zanzibar_haven.png-wvc-sizes",
  notes: "Enhanced digital presence and OTA management, driving a 40% increase in bookings for this unique eco-friendly property."
}];
const carouselImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_dashboard.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_dashboard.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_dashboard.webp-wvc-sizes",
  alt: "Revenue Dashboard"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_tech_stack.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_tech_stack.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_tech_stack.webp-wvc-sizes",
  alt: "Tech Stack"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_ota_strategy.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_ota_strategy.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_ota_strategy.webp-wvc-sizes",
  alt: "OTA Strategy"
}];
export default function Revenuecasestudies() {
  const [selectedEras, setSelectedEras] = useState<string[]>([]);
  const [priceMax, setPriceMax] = useState(5000);
  const toggleEra = (era: string) => {
    setSelectedEras(prev => prev.includes(era) ? prev.filter(e => e !== era) : [...prev, era]);
  };
  const clearAll = () => {
    setSelectedEras([]);
    setPriceMax(5000);
  };
  return <section data-section-id="3335" id="revenuecasestudies" className="bg-background text-foreground font-default">
      <div className="px-6 md:px-12 lg:px-16 py-20 md:py-32">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-7 md:col-start-2">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6 md:mb-8">
              N° 01 — Success Stories
            </p>
            <h1 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground">
              Proven <em className="italic font-light">revenue</em>
              <br />
              growth results.
            </h1>
          </div>

          <div className="col-span-12 md:col-span-3 md:col-start-10 flex flex-col gap-4 md:items-end md:text-right justify-end">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Showing 6 of 12 Case Studies
            </p>
            <div className="flex items-center gap-2 md:justify-end">
              <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
                Sort by:
              </span>
              <Select defaultValue="recent">
                <SelectTrigger size="sm" className="font-mono text-xs uppercase tracking-[0.1em] border-0 border-b border-border bg-transparent px-0 h-auto py-1 focus-visible:ring-0 hover:bg-transparent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="font-mono text-xs">
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="growth-high">Growth: High to Low</SelectItem>
                  <SelectItem value="growth-low">Growth: Low to High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-md border-y border-border">
        <div className="px-6 md:px-12 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-center lg:h-[72px] py-4 lg:py-0 gap-4 lg:gap-0 overflow-x-auto">
            <div className="flex items-center gap-3 lg:pr-6 lg:border-r lg:border-border lg:h-full">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground shrink-0">
                Category
              </span>
              <div className="flex gap-2 flex-wrap">
                {eras.map((era, i) => <Button key={era} data-index={i} onClick={() => toggleEra(era)} variant="outline" size="sm" className={`font-mono text-[10px] uppercase tracking-[0.15em] rounded-lg border h-7 px-3 transition-colors ${selectedEras.includes(era) ? "bg-secondary text-white border-secondary hover:bg-secondary/90" : "border-border text-foreground hover:border-secondary/40 hover:bg-transparent"}`}>
                    {era}
                  </Button>)}
              </div>
            </div>

            <div className="flex items-center gap-3 lg:px-6 lg:border-r lg:border-border lg:h-full">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground shrink-0">
                Growth
              </span>
              <div className="flex items-center gap-3 min-w-[180px]">
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-foreground">
                  10%
                </span>
                <RangeInput min={10} max={100} step={5} value={priceMax} onValueChange={setPriceMax} className="flex-1 h-1 bg-border accent-secondary" />
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-foreground">
                  {priceMax}%
                </span>
              </div>
            </div>

            <div className="lg:ml-auto lg:pl-6">
              <Button variant="ghost" size="sm" onClick={clearAll} className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-secondary hover:bg-transparent px-0 h-auto py-1">
                Clear all
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
          {properties.map((property, i) => <article key={property.id} data-index={i} className="group flex flex-col">
              <div className="relative overflow-hidden aspect-[4/5] bg-muted rounded-lg">
                <div className="absolute top-0 left-0 right-0 h-px bg-secondary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />
                <img src={property.image} data-wvc-srcset={property.srcset} data-wvc-sizes={property.sizes} alt={property.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                {property.badge && <Badge className={`absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.15em] rounded-lg px-2.5 py-1 border-0 ${property.badgeType === "new" ? "bg-secondary text-white" : "bg-primary text-white"}`}>
                    {property.badge}
                  </Badge>}
              </div>

              <div className="flex items-baseline justify-between mt-6 gap-4">
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                  {property.era}
                </span>
                <span className="font-default text-2xl text-secondary font-semibold">
                  {property.price}
                </span>
              </div>

              <h3 className="font-default text-[22px] leading-tight mt-3 text-foreground font-semibold">
                {property.name}
              </h3>

              <p className="text-sm text-muted-foreground mt-2 font-default">
                {property.address}
              </p>

              <div className="mt-4 pt-4 border-t border-border">
                <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-foreground/80">
                  {property.specs}
                </p>
              </div>

              <div className="flex items-center justify-between mt-5">
                <Link to="/OurServicesPage" className="font-mono text-[11px] uppercase tracking-[0.18em] text-secondary border-b border-secondary/30 pb-0.5 hover:border-secondary transition-colors">
                  View Details →
                </Link>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground hover:text-secondary hover:bg-transparent px-0 h-auto py-1 gap-1.5">
                      Quick View
                      <Plus className="size-3" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent showCloseButton={false} className="max-w-5xl w-[calc(100%-2rem)] p-0 bg-background border-border rounded-lg overflow-hidden gap-0">
                    <div className="grid grid-cols-1 md:grid-cols-5 max-h-[85vh]">
                      <div className="md:col-span-3 bg-muted relative">
                        <Carousel className="h-full">
                          <CarouselContent className="ml-0 h-full">
                            {carouselImages.map((img, idx) => <CarouselItem key={idx} data-index={idx} className="pl-0">
                                <div className="aspect-[4/5] md:aspect-auto md:h-[85vh] w-full">
                                  <img src={img.src} data-wvc-srcset={img.srcset} data-wvc-sizes={img.sizes} alt={img.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                </div>
                              </CarouselItem>)}
                          </CarouselContent>
                          <CarouselPrevious className="left-4 bg-background/90 border-border rounded-lg size-9" />
                          <CarouselNext className="right-4 bg-background/90 border-border rounded-lg size-9" />
                        </Carousel>
                      </div>

                      <div className="md:col-span-2 relative overflow-y-auto max-h-[85vh]">
                        <div className="absolute top-4 right-4 z-10">
                          <DialogClose asChild>
                            <Button variant="ghost" size="icon" className="size-8 rounded-lg hover:bg-muted">
                              <X className="size-4" />
                              <span className="sr-only">Close</span>
                            </Button>
                          </DialogClose>
                        </div>
                        <div className="p-8 md:p-10">
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
                            {property.era}
                          </p>
                          <h2 className="font-default text-3xl md:text-4xl leading-tight text-foreground font-bold">
                            {property.name}
                          </h2>
                          <p className="text-sm text-muted-foreground mt-2 font-default">
                            {property.address}
                          </p>
                          <p className="font-default text-3xl text-secondary mt-6 font-semibold">
                            {property.price}
                          </p>

                          <div className="mt-8 pt-6 border-t border-border grid grid-cols-2 gap-y-4 gap-x-6">
                            {[{
                          label: "Strategy",
                          value: "OTA"
                        }, {
                          label: "Focus",
                          value: "Revenue"
                        }, {
                          label: "Tech",
                          value: "Integrated"
                        }, {
                          label: "Audit",
                          value: "Complete"
                        }, {
                          label: "Timeline",
                          value: "6 Months"
                        }, {
                          label: "Expert",
                          value: "Sunil Singh"
                        }].map((spec, idx) => <div key={spec.label} data-index={idx}>
                                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                                  {spec.label}
                                </p>
                                <p className="font-default text-lg text-foreground mt-1 font-medium">
                                  {spec.value}
                                </p>
                              </div>)}
                          </div>

                          <div className="mt-8 pt-6 border-t border-border">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">
                              Consultant Notes
                            </p>
                            <p className="text-sm leading-relaxed text-foreground/80 font-default">
                              {property.notes}
                            </p>
                          </div>

                          <div className="mt-8 pt-6 border-t border-border">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
                              Lead Consultant
                            </p>
                            <p className="font-default text-lg text-foreground font-semibold">
                              Sunil Singh
                            </p>
                            <p className="text-sm text-muted-foreground font-default">
                              Revenue Management Expert
                            </p>
                          </div>

                          <div className="mt-8 flex flex-col gap-3">
                            <Button className="w-full rounded-lg h-12 font-mono text-[11px] uppercase tracking-[0.2em] bg-secondary text-white hover:bg-secondary/90">
                              Book a Consultation
                            </Button>
                            <Button variant="ghost" className="w-full rounded-lg h-12 font-mono text-[11px] uppercase tracking-[0.2em] border border-border hover:bg-muted">
                              View Full Case Study
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </article>)}
        </div>

        <div className="mt-20 md:mt-24 flex justify-center">
          <Pagination>
            <PaginationContent className="gap-2">
              <PaginationItem>
                <PaginationPrevious href="#" className="font-mono text-[11px] uppercase tracking-[0.18em] rounded-lg border-0 hover:bg-transparent hover:text-secondary text-muted-foreground gap-1 px-2" />
              </PaginationItem>
              {["01", "02", "03", "04"].map((page, i) => <PaginationItem key={page} data-index={i}>
                  <PaginationLink href="#" isActive={i === 0} className={`font-mono text-[11px] uppercase tracking-[0.15em] rounded-lg border-0 size-9 ${i === 0 ? "text-secondary bg-transparent" : "text-muted-foreground hover:text-secondary hover:bg-transparent"}`}>
                    {page}
                  </PaginationLink>
                </PaginationItem>)}
              <PaginationItem>
                <PaginationNext href="#" className="font-mono text-[11px] uppercase tracking-[0.18em] rounded-lg border-0 hover:bg-transparent hover:text-secondary text-foreground gap-1 px-2" />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    </section>;
}